import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";

// Detects Web Speech API support (iOS Safari 14+, Chrome, Edge, most modern browsers)
function getRecognitionCtor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function VoiceInput({ onTranscript, onFinal, disabled, lang = "it-IT" }) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const finalTextRef = useRef("");

  useEffect(() => {
    setSupported(!!getRecognitionCtor());
  }, []);

  const start = () => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      toast.error("Il tuo browser non supporta il microfono. Prova Chrome o Safari aggiornati.");
      return;
    }
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    finalTextRef.current = "";

    rec.onresult = (e) => {
      let interim = "";
      let final = finalTextRef.current;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      finalTextRef.current = final;
      onTranscript && onTranscript((final + interim).trim());
    };
    rec.onerror = (ev) => {
      setListening(false);
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        toast.error("Accesso al microfono negato. Consenti l'accesso dal browser.");
      } else if (ev.error !== "aborted" && ev.error !== "no-speech") {
        toast.error(`Errore microfono: ${ev.error}`);
      }
    };
    rec.onend = () => {
      setListening(false);
      const finalText = finalTextRef.current.trim();
      if (finalText && onFinal) onFinal(finalText);
    };

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (err) {
      console.warn("VoiceInput start failed", err);
      toast.error("Impossibile avviare il microfono");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (err) { console.warn("VoiceInput stop", err); }
    setListening(false);
  };

  if (!supported) {
    return (
      <button
        type="button"
        title="Il tuo browser non supporta il microfono"
        disabled
        data-testid="voice-btn-unsupported"
        className="h-10 w-10 flex items-center justify-center rounded-xl text-[#8A8A8A] cursor-not-allowed"
      >
        <MicOff className="w-4 h-4" />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={listening ? "voice-btn-stop" : "voice-btn-start"}
      onClick={listening ? stop : start}
      disabled={disabled}
      title={listening ? "Ferma registrazione" : "Registra domanda"}
      className={`h-10 w-10 flex items-center justify-center rounded-xl transition-colors ${
        listening
          ? "bg-[#FF3333] text-white hover:bg-[#DD2222]"
          : "text-[#0A0A0A] hover:bg-black/5 border border-black/15"
      }`}
    >
      {listening ? (
        <span className="relative">
          <Mic className="w-4 h-4" />
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full animate-pulse" />
        </span>
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </button>
  );
}
